import getKlaviyoProfile from "@/actions/klaviyo/getKlaviyoProfile"
import isKlaviyoIdSubscribed from "@/actions/klaviyo/isKlaviyoIdSubscribed"
import { changePaymentFields } from "@/actions/payment/change-payment-fields"
import { CustomerInfo } from "@/app/(site)/contexts/checkout/types"
import { CheckoutAPISelectionWithRequiredToken } from "@/types/centra/checkout-api/selection/get-selection"
import { Result } from "@/types/generic/Result"
import React from "react"

export const useValidateEmailDebounce = (
    addFreeShipping: () => Promise<
        Result<CheckoutAPISelectionWithRequiredToken, { message: string }>
    >,
    removeFreeShipping: () => Promise<
        Result<CheckoutAPISelectionWithRequiredToken, { message: string }>
    >
) => {
    const [validKlaviyoEmail, setValidKlaviyoEmail] = React.useState<boolean | null>(null)

    const validateIsEmailSignedUpToKlaviyo = async (
        email: string
    ): Promise<boolean | null> => {
        try {
            // Get the Klaviyo profile by the email
            const response = await getKlaviyoProfile(email)

            // Check if the response data is valid and extract the id
            if (response.data && response.data.length > 0 && response.data[0].id) {
                const profileId = response.data[0].id
                try {
                    const match = await isKlaviyoIdSubscribed(profileId)
                    if (!match) {
                        setValidKlaviyoEmail(false)
                        removeFreeShipping()
                        return false
                    }
                    setValidKlaviyoEmail(true)
                    addFreeShipping()
                    return match
                } catch (error) {
                    console.warn("Could not check Klaviyo profile subscription", error)
                    setValidKlaviyoEmail(false)
                    removeFreeShipping()
                    return null // Return null if there is an error
                }
            } else {
                setValidKlaviyoEmail(false)
                removeFreeShipping()
                return false // Return false if no profile found
            }
        } catch (error) {
            console.warn("Could not get Klaviyo profile", error)
            setValidKlaviyoEmail(false)
            removeFreeShipping()
            return null // Return null in case of an error
        }
    }

    const callChangePaymentFields = async (data: CustomerInfo) => {
        const { billingAddress, shippingAddress } = data

        const nullForEmpty = (value: string | undefined) => {
            return value === "" || value === undefined ? null : value
        }

        await changePaymentFields({
            address: {
                ...billingAddress,
                email: nullForEmpty(billingAddress.email),
                firstName: nullForEmpty(billingAddress.firstName),
                lastName: nullForEmpty(billingAddress.lastName),
                address1: nullForEmpty(billingAddress.address1),
                phoneNumber: nullForEmpty(billingAddress.phoneNumber),
                zipCode: nullForEmpty(billingAddress.zipCode),
                city: nullForEmpty(billingAddress.city),
                country: nullForEmpty(billingAddress.country),
                state: nullForEmpty(billingAddress.state)
            },
            shippingAddress: {
                ...shippingAddress,
                firstName: nullForEmpty(shippingAddress.firstName),
                lastName: nullForEmpty(shippingAddress.lastName),
                address1: nullForEmpty(shippingAddress.address1),
                phoneNumber: nullForEmpty(shippingAddress.phoneNumber),
                zipCode: nullForEmpty(shippingAddress.zipCode),
                email: nullForEmpty(shippingAddress.email),
                city: nullForEmpty(shippingAddress.city),
                country: nullForEmpty(shippingAddress.country),
                state: nullForEmpty(shippingAddress.state)
            }
        })
    }

    return {
        validKlaviyoEmail,
        setValidKlaviyoEmail,
        callChangePaymentFields,
        validateIsEmailSignedUpToKlaviyo
    }
}

export const isValidStreetName = (streetName: string) => {
    const unallowedSpecialChars = new RegExp(/[!$%*\\<=>?\[\]^{|}~]/)
    const isValid = !unallowedSpecialChars.test(streetName)
    return isValid
}

export const exceedMaxLength = (value: string) => {
    return !/^[\s\S]{0,35}$/.test(value)
}
